import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getStoredReferralCode, clearReferralCode } from '@/lib/referral';
import {
  signInWithGoogle,
  initializeGoogleWebButton,
  classifyGoogleAuthError,
  isGoogleSignInCancelled,
} from '@/lib/googleAuth';
import { isNativePlatform } from '@/lib/providerPush';
import PhoneCountryInput, { validatePhoneCountryInput } from '@/components/auth/PhoneCountryInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Shared Google authentication flow for browser and Android login.
 * Browser users get Google's official web button; Android keeps the native
 * Capacitor sign-in flow. Both send the same ID token to the backend.
 */
export default function GoogleAuthSection({
  role,
  onAuthenticated,
  googleButtonTestId,
  registerNameTestId,
  registerSubmitTestId,
}) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('google');
  const [registrationToken, setRegistrationToken] = useState('');
  const [linkToken, setLinkToken] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('IN');
  const [phone, setPhone] = useState('');
  const [googleProfile, setGoogleProfile] = useState(null);
  const [webButtonError, setWebButtonError] = useState('');
  const webButtonRef = useRef(null);
  const webCredentialHandlerRef = useRef(null);

  const showError = (error) => {
    const message = classifyGoogleAuthError(error);
    if (message) toast.error(message);
  };

  const processGoogleCredential = async (idToken) => {
    const { data } = await api.post('/auth/google', { id_token: idToken, role });
    if (data.status === 'authenticated') {
      onAuthenticated(data);
      return;
    }
    if (data.status === 'link_required') {
      setLinkToken(data.link_token);
      setMaskedEmail(data.email || '');
      setStep('link');
      return;
    }
    if (data.status === 'registration_required') {
      setRegistrationToken(data.registration_token);
      setGoogleProfile(data.google_profile || {});
      setName(data.google_profile?.name || '');
      setStep('register');
      return;
    }
    toast.error('Unexpected authentication response');
  };

  webCredentialHandlerRef.current = async (idToken) => {
    setLoading(true);
    try {
      await processGoogleCredential(idToken);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isNativePlatform() || step !== 'google') return undefined;

    let active = true;
    const element = webButtonRef.current;
    if (!element) return undefined;

    setWebButtonError('');
    initializeGoogleWebButton(element, (idToken) => {
      if (active) {
        void webCredentialHandlerRef.current?.(idToken);
      }
    }).catch((error) => {
      if (!active) return;
      const message = String(error?.message || '');
      setWebButtonError(
        message.includes('REACT_APP_GOOGLE_WEB_CLIENT_ID')
          ? 'Google sign-in is not configured for the website yet.'
          : message || 'Google sign-in could not load. Please refresh and try again.'
      );
    });

    return () => {
      active = false;
      element.replaceChildren();
    };
  }, [role, step]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const idToken = await signInWithGoogle();
      await processGoogleCredential(idToken);
    } catch (e) {
      if (!isGoogleSignInCancelled(e)) showError(e);
    } finally {
      setLoading(false);
    }
  };

  const completeRegistration = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    const phoneCheck = validatePhoneCountryInput(countryCode, phone);
    if (!phoneCheck.ok) {
      toast.error(phoneCheck.error);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/google/register', {
        registration_token: registrationToken,
        name: name.trim(),
        country_code: countryCode,
        phone: phoneCheck.digits,
        referral_code: getStoredReferralCode() || undefined,
      });
      clearReferralCode();
      onAuthenticated(data);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const confirmLink = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/google/link', { link_token: linkToken });
      onAuthenticated(data);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'register') {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Complete your RESQLY profile</h3>
          <p className="text-xs text-slate-500 mt-1">
            Phone number is required for operational contact (not shown to {role === 'provider' ? 'customers' : 'providers'}).
          </p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">Name</label>
          <Input
            data-testid={registerNameTestId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            className="mt-1"
          />
        </div>
        {googleProfile?.email && (
          <p className="text-xs text-slate-500">Google email: {googleProfile.email}</p>
        )}
        <PhoneCountryInput
          countryCode={countryCode}
          onCountryCodeChange={setCountryCode}
          phone={phone}
          onPhoneChange={setPhone}
          countryTestId={`${role}-google-country`}
          phoneTestId={`${role}-google-phone`}
        />
        <Button
          data-testid={registerSubmitTestId}
          disabled={loading}
          onClick={completeRegistration}
          className="w-full bg-blue-700 hover:bg-blue-800"
        >
          {loading ? 'Continuing...' : 'Continue'}
        </Button>
        <button
          type="button"
          onClick={() => setStep('google')}
          className="text-xs text-slate-500 underline w-full text-center"
        >
          Back
        </button>
      </div>
    );
  }

  if (step === 'link') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-700">
          A RESQLY account already exists for <b>{maskedEmail}</b>. Link Google sign-in to that account?
        </p>
        <Button
          data-testid={`${role}-google-link`}
          disabled={loading}
          onClick={confirmLink}
          className="w-full bg-blue-700 hover:bg-blue-800"
        >
          {loading ? 'Linking...' : 'Link Google account'}
        </Button>
        <button
          type="button"
          onClick={() => setStep('google')}
          className="text-xs text-slate-500 underline w-full text-center"
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {isNativePlatform() ? (
        <Button
          data-testid={googleButtonTestId}
          disabled={loading}
          onClick={handleGoogleSignIn}
          className="w-full bg-white text-slate-800 border border-slate-200 hover:bg-slate-50"
          variant="outline"
        >
          {loading ? 'Signing in...' : 'Continue with Google'}
        </Button>
      ) : (
        <div className="w-full flex flex-col items-center" data-testid={googleButtonTestId}>
          <div ref={webButtonRef} className="w-full min-h-10 flex justify-center" />
          {webButtonError && (
            <p role="alert" className="w-full text-center text-sm text-rose-600">
              {webButtonError}
            </p>
          )}
        </div>
      )}
      {loading && !isNativePlatform() && (
        <p className="text-center text-xs text-slate-500">Signing in securely...</p>
      )}
    </div>
  );
}
